import type {
  DriverAssignment,
  DriverAssignmentAsset,
  DriverAssignmentBranch,
  DriverAssignmentDriver,
  DriverAssignmentHistoryAction,
  DriverAssignmentHistoryItem,
} from "../domain";

export interface DriverFixture extends DriverAssignmentDriver {
  readonly status: "ACTIVE" | "INACTIVE";
}

const branches = [
  { id: "branch-jeddah", nameEn: "Jeddah Branch", nameAr: "فرع جدة" },
  { id: "branch-riyadh", nameEn: "Riyadh Branch", nameAr: "فرع الرياض" },
  { id: "branch-dammam", nameEn: "Dammam Branch", nameAr: "فرع الدمام" },
] as const satisfies readonly DriverAssignmentBranch[];

export const branchFixtures: readonly DriverAssignmentBranch[] = branches;

const driverNames: ReadonlyArray<readonly [en: string, ar: string]> = [
  ["Mohammed bin Abdullah Al-Ghamdi", "محمد بن عبدالله الغامدي"],
  ["Al-Hussein bin Ali", "الحسين بن علي"],
  ["Ali bin Abi Talib", "علي بن أبي طالب"],
  ["Hussein bin Ali", "حسين بن علي"],
  ["Youssef Ahmed Mohammed", "يوسف احمد محمد"],
  ["Mohammed Ahmed Ali", "محمد احمد علي"],
  ["Khaled Saad Al-Otaibi", "خالد سعد العتيبي"],
  ["Faisal Nasser Al-Qahtani", "فيصل ناصر القحطاني"],
  ["Abdulrahman Al-Shehri", "عبدالرحمن الشهري"],
  ["Saud Al-Dosari", "سعود الدوسري"],
  ["Omar Al-Harbi", "عمر الحربي"],
  ["Turki Al-Mutairi", "تركي المطيري"],
  ["Nawaf Al-Zahrani", "نواف الزهراني"],
  ["Majed Al-Anazi", "ماجد العنزي"],
  ["Bandar Al-Shammari", "بندر الشمري"],
  ["Sultan Al-Malki", "سلطان المالكي"],
  ["Rakan Al-Subaie", "راكان السبيعي"],
  ["Hamad Al-Juhani", "حمد الجهني"],
  ["Ziyad Al-Ruwaili", "زياد الرويلي"],
  ["Fahad Al-Balawi", "فهد البلوي"],
  ["Yasser Al-Asmari", "ياسر الأسمري"],
  ["Ibrahim Al-Saeed", "إبراهيم السعيد"],
  // Free: no active assignment.
  ["Hassan Al-Qarni", "حسن القرني"],
  ["Mansour Al-Ghamdi", "منصور الغامدي"],
  ["Adel Al-Shahrani", "عادل الشهراني"],
  ["Waleed Al-Yami", "وليد اليامي"],
];

const assetNames: ReadonlyArray<
  readonly [en: string, ar: string, plate: number]
> = [
  ["Nissan Patrol", "سيارة نيسان باترول", 7720],
  ["Toyota Land Cruiser", "سيارة تويوتا لاند كروزر", 8832],
  ["Ford Explorer", "سيارة فورد إكسبلورر", 9945],
  ["Lexus RX", "سيارة لكزس RX", 12890],
  ["Jeep Wrangler", "سيارة جيب رانجلر", 14023],
  ["Honda Pilot", "سيارة هوندا بايلوت", 11567],
  ["Chevrolet Tahoe", "سيارة شيفروليه تاهو", 10234],
  ["Hyundai Tucson", "سيارة هيونداي توسان", 15511],
  ["Kia Sorento", "سيارة كيا سورينتو", 16420],
  ["GMC Yukon", "سيارة جي إم سي يوكن", 17305],
  ["Mitsubishi Pajero", "سيارة ميتسوبيشي باجيرو", 18876],
  ["Isuzu D-Max", "شاحنة إيسوزو دي ماكس", 19042],
  ["Toyota Hilux", "شاحنة تويوتا هايلكس", 2241],
  ["Nissan Navara", "شاحنة نيسان نافارا", 3318],
  ["Ford F-150", "شاحنة فورد F-150", 4475],
  ["Hyundai H1", "حافلة هيونداي H1", 5562],
  ["Toyota Corolla", "سيارة تويوتا كورولا", 6604],
  ["Hyundai Elantra", "سيارة هيونداي إلنترا", 6789],
  ["Kia Cerato", "سيارة كيا سيراتو", 7013],
  ["Chevrolet Silverado", "شاحنة شيفروليه سيلفرادو", 8150],
  ["Mercedes Actros", "شاحنة مرسيدس أكتروس", 9207],
  ["Volvo FH", "شاحنة فولفو FH", 9381],
  // Free: no active driver.
  ["Toyota Coaster", "حافلة تويوتا كوستر", 20114],
  ["Isuzu NPR", "شاحنة إيسوزو NPR", 20562],
  ["Nissan Sunny", "سيارة نيسان صني", 21037],
  ["Toyota Camry", "سيارة تويوتا كامري", 21490],
];

/** The last driver is inactive, so the Create form must never offer them. */
export const driverFixtures: readonly DriverFixture[] = driverNames.map(
  ([nameEn, nameAr], index) => ({
    id: `driver-${index + 1}`,
    username: `driver${index + 1}`,
    nameEn,
    nameAr,
    avatarUrl: null,
    status: index === driverNames.length - 1 ? "INACTIVE" : "ACTIVE",
  }),
);

export const assetFixtures: readonly DriverAssignmentAsset[] = assetNames.map(
  ([nameEn, nameAr, plate], index) => ({
    id: `asset-${index + 1}`,
    nameEn,
    nameAr,
    code: `ص ص ط ${plate}`,
    branch: branches[index % branches.length] ?? branches[0],
  }),
);

const ASSIGNED_COUNT = 22;

/**
 * One driver per asset and one asset per driver (the business rule). 22 rows,
 * so the default page size of 10 spans three pages as in the Figma frame;
 * assignment N pairs driver N with asset N, one day older each (24 Sep
 * 2026 down to 3 Sep). Four drivers and four assets stay free for Create.
 */
export const driverAssignmentFixtures: readonly DriverAssignment[] =
  driverFixtures.slice(0, ASSIGNED_COUNT).flatMap((driver, index) => {
    const asset = assetFixtures[index];
    if (asset === undefined) return [];
    return [
      {
        id: `driver-assignment-${index + 1}`,
        driver: {
          id: driver.id,
          username: driver.username,
          nameEn: driver.nameEn,
          nameAr: driver.nameAr,
          avatarUrl: driver.avatarUrl,
        },
        asset,
        assignedAt: new Date(Date.UTC(2026, 8, 24 - index, 9)).toISOString(),
      },
    ];
  });

function at(day: string): string {
  return new Date(`${day}T09:00:00.000Z`).toISOString();
}

function pastRecord(
  id: string,
  index: number,
  action: DriverAssignmentHistoryAction,
  assignedAt: string,
  actionAt: string,
): DriverAssignmentHistoryItem[] {
  const driver = driverFixtures[index];
  const asset = assetFixtures[index];
  if (driver === undefined || asset === undefined) return [];
  return [
    {
      id,
      driver: {
        id: driver.id,
        nameEn: driver.nameEn,
        nameAr: driver.nameAr,
        avatarUrl: driver.avatarUrl,
      },
      asset,
      action,
      assignedAt,
      actionAt,
    },
  ];
}

/**
 * A "Created" row for every active assignment, plus two older pairs that
 * were created and later removed (now free drivers 23 and 24) -- 26 rows,
 * so the default page size spans three pages as in the Figma frame.
 */
export const driverAssignmentHistoryFixtures: readonly DriverAssignmentHistoryItem[] =
  [
    ...pastRecord("history-past-1", 22, "Created", at("2026-08-10"), at("2026-08-10")),
    ...pastRecord("history-past-2", 22, "Removed", at("2026-08-10"), at("2026-08-25")),
    ...pastRecord("history-past-3", 23, "Created", at("2026-08-12"), at("2026-08-12")),
    ...pastRecord("history-past-4", 23, "Removed", at("2026-08-12"), at("2026-09-01")),
    ...driverAssignmentFixtures.map((assignment, index) => ({
      id: `history-${index + 1}`,
      driver: assignment.driver,
      asset: assignment.asset,
      action: "Created" as const,
      assignedAt: assignment.assignedAt,
      actionAt: assignment.assignedAt,
    })),
  ];
